import { useCallback } from 'react';

import { useCartUpdateHandler } from '#hooks/useCartUpdateHandler';
import { useShopProvider } from '#provider/useShopProvider.js';
import { ButtonMantine, ButtonProps, notifications } from 'ui';

import { SharedCheckoutButtonsProps } from '../CheckoutButtons/CheckoutButtons.types';
import { useCheckoutButtons } from '../CheckoutButtons/useCheckoutButtons';

type AddToCartButtonArgs = {
  cartId?: string;
};
type AddToCartButtonClick = (arg0: AddToCartButtonArgs, handleCartUpdate: () => void) => void;

interface AddToCartButtonProps extends SharedCheckoutButtonsProps {
  /** Callback function to be called on click  */
  onAddToCartClick?: AddToCartButtonClick;
  disabled?: boolean;
  buttonProps?: ButtonProps;
}

const AddToCartButton = ({
  storeId,
  storeItemId,
  validateOnClick,
  cartItems,
  buttonProps = { children: 'Add To Cart' },
  onAddToCartClick,
  onOptions,
  onCartCreate,
  cartFields = {},
  quantity,
  scopeId,
  onSuccess,
}: AddToCartButtonProps) => {
  const { hasOptions } = useCheckoutButtons({ storeId, storeItemId });

  const { handleCartUpdate, isLoading, cartId } = useCartUpdateHandler({
    storeId,
    storeItemId,
    cartFields,
    cartItems,
    quantity,
    validateOnClick,
    onOptions,
    onCartCreate,
    hasOptions,
    scopeId,
    onSuccess,
  });

  const handleCartClick = useCallback(() => {
    if (onAddToCartClick) {
      onAddToCartClick({ cartId }, handleCartUpdate);
    } else {
      handleCartUpdate();
    }
  }, [cartId, handleCartUpdate, onAddToCartClick]);

  return (
    <ButtonMantine
      onClick={handleCartClick}
      variant="outline"
      color="cobalt"
      {...buttonProps}
      loading={isLoading || buttonProps.loading}
    >
      {buttonProps.children}
    </ButtonMantine>
  );
};

const AddToCartButtonWrapper = ({ storeId, storeItemId, ...props }: AddToCartButtonProps) => {
  const { isAddToCartAvailable, unavailabilityReasons } = useShopProvider();

  if (!storeId || !storeItemId) return null;

  return (
    <AddToCartButton
      storeId={storeId}
      storeItemId={storeItemId}
      {...props}
      onAddToCartClick={
        isAddToCartAvailable
          ? props.onAddToCartClick
          : () => {
              notifications.show({
                message: unavailabilityReasons?.addToCart ?? 'Add To Cart is not available.',
              });
            }
      }
    />
  );
};

export { AddToCartButtonWrapper as AddToCartButton };
export type { AddToCartButtonProps };
